import type { Middleware, PayloadAction, Reducer } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import fp from "lodash/fp"
import type { GridSortModel } from "@mui/x-data-grid"
import type { RootState } from "."

interface IDefaultTable {
  qtyRows: number
  // In the data grid's own shape, so a table hands it over as is. Kept for the session only:
  // the middleware below persists qtyRows alone.
  sortModel: GridSortModel
}

interface TablesState {
  alertsTable: IDefaultTable
  devicesTable: IDefaultTable
  powerlinesTable: IDefaultTable
  // The alert lists of the org and of groups, and those of a device, power line or tower.
  alertAccordions: { qtyRows: number }
  entityAlertAccordions: { qtyRows: number }
}

const initialState: TablesState = {
  alertsTable: {
    qtyRows: 10,
    sortModel: [{ field: "timestamp", sort: "desc" }],
  },
  devicesTable: {
    qtyRows: 10,
    sortModel: [{ field: "alertStatus", sort: "desc" }],
  },
  powerlinesTable: {
    qtyRows: 10,
    sortModel: [{ field: "powerline", sort: "asc" }],
  },
  alertAccordions: {
    qtyRows: 10,
  },
  entityAlertAccordions: {
    qtyRows: 5,
  },
}

function getPersistentTables() {
  // Lazy load the state from the localStorage or fallback to the initialState
  try {
    const state = JSON.parse(localStorage.getItem("tables") as string)
    const {
      alertsTable,
      devicesTable,
      powerlinesTable,
      alertAccordions,
      entityAlertAccordions,
    } = state

    return fp.merge(initialState, {
      alertsTable,
      devicesTable,
      powerlinesTable,
      alertAccordions,
      entityAlertAccordions,
    })
  } catch (_error) {
    return initialState
  }
}

export const tablesSlice = createSlice({
  name: "tables",
  initialState: getPersistentTables,
  reducers: {
    /*-- Alerts table --*/
    setAlertsTableRowsAmount: (state, action: PayloadAction<number>): TablesState => ({
      ...state,
      alertsTable: { ...state.alertsTable, qtyRows: action.payload },
    }),
    setAlertsTableSortModel: (
      state,
      action: PayloadAction<GridSortModel>,
    ): TablesState => ({
      ...state,
      alertsTable: { ...state.alertsTable, sortModel: action.payload },
    }),

    /*-- Devices table --*/
    setDevicesTableRowsAmount: (state, action: PayloadAction<number>): TablesState => ({
      ...state,
      devicesTable: { ...state.devicesTable, qtyRows: action.payload },
    }),
    setDevicesTableSortModel: (
      state,
      action: PayloadAction<GridSortModel>,
    ): TablesState => ({
      ...state,
      devicesTable: { ...state.devicesTable, sortModel: action.payload },
    }),

    /*-- Powerlines table --*/
    setPowerlinesTableRowsAmount: (
      state,
      action: PayloadAction<number>,
    ): TablesState => ({
      ...state,
      powerlinesTable: { ...state.powerlinesTable, qtyRows: action.payload },
    }),
    setPowerlinesTableSortModel: (
      state,
      action: PayloadAction<GridSortModel>,
    ): TablesState => ({
      ...state,
      powerlinesTable: { ...state.powerlinesTable, sortModel: action.payload },
    }),

    /*-- Alert accordions --*/
    setAlertAccordionsRowsAmount: (
      state,
      action: PayloadAction<number>,
    ): TablesState => ({
      ...state,
      alertAccordions: { qtyRows: action.payload },
    }),
    setEntityAlertAccordionsRowsAmount: (
      state,
      action: PayloadAction<number>,
    ): TablesState => ({
      ...state,
      entityAlertAccordions: { qtyRows: action.payload },
    }),
  },
})

export const {
  setAlertsTableRowsAmount,
  setAlertsTableSortModel,
  setDevicesTableRowsAmount,
  setDevicesTableSortModel,
  setPowerlinesTableRowsAmount,
  setPowerlinesTableSortModel,
  setAlertAccordionsRowsAmount,
  setEntityAlertAccordionsRowsAmount,
} = tablesSlice.actions
export const tablesReducer: Reducer<TablesState> = tablesSlice.reducer
export default tablesReducer

export const selectAlertsTable = (state: RootState) => state.tables.alertsTable
export const selectDevicesTable = (state: RootState) => state.tables.devicesTable
export const selectPowerlinesTable = (state: RootState) => state.tables.powerlinesTable
export const selectAlertAccordions = (state: RootState) => state.tables.alertAccordions
export const selectEntityAlertAccordions = (state: RootState) =>
  state.tables.entityAlertAccordions

// Store the table in localStorage, while keeping the reducers as pure functions
export const tablesMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action)
  if (typeof action === "object" && action !== null && "type" in action) {
    switch (action.type) {
      case "tables/setAlertsTableRowsAmount":
      case "tables/setDevicesTableRowsAmount":
      case "tables/setPowerlinesTableRowsAmount":
      case "tables/setAlertAccordionsRowsAmount":
      case "tables/setEntityAlertAccordionsRowsAmount": {
        const tableState = store.getState()

        const tables = {
          alertsTable: {
            qtyRows: tableState.tables.alertsTable.qtyRows,
          },
          devicesTable: {
            qtyRows: tableState.tables.devicesTable.qtyRows,
          },
          powerlinesTable: {
            qtyRows: tableState.tables.powerlinesTable.qtyRows,
          },
          alertAccordions: {
            qtyRows: tableState.tables.alertAccordions.qtyRows,
          },
          entityAlertAccordions: {
            qtyRows: tableState.tables.entityAlertAccordions.qtyRows,
          },
        }
        localStorage.setItem("tables", JSON.stringify(tables))
        break
      }
      default:
        break
    }
  }
  return result
}
